/**
 * The Schematics view's 3D stage: one schematic on a soft spotlight, in its own small three.js renderer (the map's
 * engine rests while the view is open). Blocks are meshed by the world workers with the person's own textures
 * (engine/materials.ts, same shaders as on the map); without textures ("Map colours only") the outer blocks are
 * drawn as coloured cubes. Also renders the filmstrip thumbnails, off screen, with the same renderer.
 */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { AssetBundle } from "@mcp/assets";
import type { MeshBuffers } from "@mcp/mesher";
import { createBlockTexture, createStructureMaterials, type SharedUniforms, type StructureMaterials } from "../../engine/materials.ts";
import type { StructureMeshResult, StructureVoxels } from "../../engine/protocol.ts";

export interface StageModel {
  group: THREE.Group;
  /** size in blocks (x, y, z) */
  size: [number, number, number];
  /** blocks per drawn cube edge (1 = full detail) */
  lod: number;
  dispose(): void;
}

type Mats = Pick<StructureMaterials, "solid" | "translucent">;

function geometry(b: MeshBuffers): THREE.BufferGeometry | null {
  if (!b.count) return null;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(b.pos, 3));
  g.setAttribute("uvl", new THREE.BufferAttribute(b.uvl, 4));
  g.setAttribute("col", new THREE.BufferAttribute(b.col, 4, true));
  g.setIndex(new THREE.BufferAttribute(b.indices, 1));
  return g;
}

/** Real block models (meshed in a world worker). `lod` > 1: the worker meshed a coarser copy. */
export function meshModel(r: StructureMeshResult, lod: number, mats: Mats, size: [number, number, number]): StageModel {
  const group = new THREE.Group();
  const geos: THREE.BufferGeometry[] = [];
  for (const t of r.tiles) {
    const add = (g: THREE.BufferGeometry | null, mat: THREE.Material, order: number) => {
      if (!g) return;
      geos.push(g);
      const m = new THREE.Mesh(g, mat);
      m.position.set(t.cx * 16, 0, t.cz * 16);
      m.renderOrder = order;
      group.add(m);
    };
    add(geometry(t.solid), mats.solid, 1);
    add(geometry(t.translucent), mats.translucent, 2);
  }
  group.scale.setScalar(lod);
  return { group, size, lod, dispose: () => { for (const g of geos) g.dispose(); } };
}

/** Coloured cubes (no game textures): Minecraft-like face shading baked into the cube, colour per block. */
export function voxelModel(v: StructureVoxels, colour: (name: string) => number, size: [number, number, number]): StageModel {
  const group = new THREE.Group();
  const n = v.pal.length;
  const box = new THREE.BoxGeometry(1, 1, 1);
  box.translate(0.5, 0.5, 0.5);
  // BoxGeometry faces: +x, -x, +y, -y, +z, -z (4 vertices each)
  const face = [0.8, 0.8, 1, 0.5, 0.64, 0.64];
  const shade = new Float32Array(24 * 3);
  for (let i = 0; i < 24; i++) shade.fill(face[i >> 2], i * 3, i * 3 + 3);
  box.setAttribute("color", new THREE.BufferAttribute(shade, 3));
  const mat = new THREE.MeshBasicMaterial({ vertexColors: true });
  const mesh = new THREE.InstancedMesh(box, mat, Math.max(1, n));
  mesh.count = n;
  const m4 = new THREE.Matrix4();
  const col = new THREE.Color();
  const byPal = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    m4.makeTranslation(v.pos[i * 3], v.pos[i * 3 + 1], v.pos[i * 3 + 2]);
    mesh.setMatrixAt(i, m4);
    const p = v.pal[i];
    let c = byPal.get(p);
    if (c === undefined) { c = colour(v.names[p] ?? ""); byPal.set(p, c); }
    mesh.setColorAt(i, col.setRGB(((c >> 16) & 255) / 255, ((c >> 8) & 255) / 255, (c & 255) / 255, THREE.LinearSRGBColorSpace));
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.frustumCulled = false;
  group.add(mesh);
  group.scale.setScalar(v.lod);
  return { group, size, lod: v.lod, dispose: () => { box.dispose(); mat.dispose(); mesh.dispose(); } };
}

/** soft round shadow under the build (a radial gradient on a plane) */
function shadowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 64);
  grad.addColorStop(0, "rgba(0,0,0,0.55)");
  grad.addColorStop(0.55, "rgba(0,0,0,0.28)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

const FOV = 30;

/** a model in its turn/mirror pivot, centred on the origin, with its shadow */
function rig(m: StageModel, shadowTex: THREE.Texture) {
  const [w, h, l] = m.size;
  const pivot = new THREE.Group();
  const holder = new THREE.Group();
  holder.position.set(-w / 2, 0, -l / 2);
  holder.add(m.group);
  pivot.add(holder);
  const sh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false, color: 0x000000 }));
  sh.rotation.x = -Math.PI / 2;
  sh.position.y = -0.04;
  sh.scale.set(w * 1.5 + 6, l * 1.5 + 6, 1);
  sh.renderOrder = 0;
  pivot.add(sh);
  return { pivot, radius: 0.5 * Math.hypot(w, h, l), height: h, dispose: () => { sh.geometry.dispose(); (sh.material as THREE.Material).dispose(); } };
}

/** camera direction for the default 3D view: from the south-east, a bit above */
function viewDir(top: boolean) {
  if (top) return new THREE.Vector3(0, 1, 0.0001).normalize();
  const el = THREE.MathUtils.degToRad(28), az = THREE.MathUtils.degToRad(40);
  return new THREE.Vector3(Math.cos(el) * Math.sin(az), Math.sin(el), Math.cos(el) * Math.cos(az));
}

export class SchematicStage {
  readonly renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(FOV, 1, 0.1, 10000);
  private controls: OrbitControls;
  private shared: SharedUniforms;
  readonly mats: Mats;
  readonly textured: boolean;
  private shadowTex = shadowTexture();
  private current: { model: StageModel; rig: ReturnType<typeof rig> } | null = null;
  private raf = 0;
  private dirty = true;
  private top = false;
  private turns = 0;
  private mirror = false;
  private disposed = false;
  get alive() { return !this.disposed; }
  /** the view moved (orbit / zoom): the hint can fade */
  onInteract: () => void = () => {};

  constructor(canvas: HTMLCanvasElement, textures: AssetBundle["textures"] | null) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // the block shaders output game-space colours directly
    this.renderer.setClearColor(0x000000, 0);
    this.textured = !!textures;
    const tex = textures ? createBlockTexture(textures, this.renderer) : new THREE.DataArrayTexture(new Uint8Array([128, 128, 128, 255]), 1, 1, 1);
    tex.needsUpdate = true;
    this.shared = {
      uTex: { value: tex },
      uFogColor: { value: new THREE.Color(0, 0, 0) },
      uFogNear: { value: 1e7 },
      uFogFar: { value: 2e7 },
      uCutY: { value: 1e9 },
      uTime: { value: 0 },
      uLightMix: { value: 0 },
      uDaylight: { value: 1 },
      uSkyLight: { value: new THREE.Color(1, 1, 1) },
      uTorch: { value: new THREE.Color(1.0, 0.82, 0.55) },
      uMinLight: { value: 0.07 },
    };
    const m = createStructureMaterials(this.shared, new THREE.Color(1, 1, 1), 0, 1);
    m.depth.dispose();
    this.mats = { solid: m.solid, translucent: m.translucent };
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;
    this.controls.enablePan = false;
    this.controls.rotateSpeed = 0.8;
    this.controls.zoomSpeed = 0.9;
    this.controls.addEventListener("start", () => this.onInteract());
    this.controls.addEventListener("change", () => { this.dirty = true; });
    this.loop();
  }

  /** Show a model (null = nothing). `keepView`: same build again (e.g. a finer copy), keep the camera. */
  setModel(model: StageModel | null, keepView = false) {
    if (this.current) {
      this.scene.remove(this.current.rig.pivot);
      this.current.rig.dispose();
      this.current.model.dispose();
      this.current = null;
    }
    if (model) {
      const r = rig(model, this.shadowTex);
      this.current = { model, rig: r };
      this.scene.add(r.pivot);
      this.orient();
      if (!keepView) this.frame();
    }
    this.dirty = true;
  }

  setOrientation(turns: number, mirror: boolean) {
    this.turns = turns;
    this.mirror = mirror;
    this.orient();
  }

  /** 3D (orbit) or straight from above */
  setTop(top: boolean) {
    this.top = top;
    this.frame();
  }

  private orient() {
    const p = this.current?.rig.pivot;
    if (!p) return;
    p.rotation.set(0, (-this.turns * Math.PI) / 2, 0);
    p.scale.set(this.mirror ? -1 : 1, 1, 1);
    this.dirty = true;
  }

  /** camera to the default view of the current model */
  frame() {
    const r = this.current?.rig;
    if (!r) return;
    const c = this.controls;
    // fit the bounding sphere into the narrower side, with room around it (the stage's chips sit at the edges)
    const fit = Math.min(1, this.camera.aspect);
    const dist = (r.radius / Math.sin(THREE.MathUtils.degToRad(FOV / 2))) / fit * (this.top ? 1.1 : 1.3);
    c.target.set(0, this.top ? 0 : r.height * 0.46, 0);
    this.camera.position.copy(c.target).addScaledVector(viewDir(this.top), dist);
    c.minDistance = Math.max(2, r.radius * 0.5);
    c.maxDistance = Math.max(40, r.radius * 6);
    c.minPolarAngle = 0;
    c.maxPolarAngle = this.top ? 0 : Math.PI * 0.49;
    this.camera.near = Math.max(0.05, dist / 500);
    this.camera.far = dist * 20;
    this.camera.updateProjectionMatrix();
    c.update();
    this.dirty = true;
  }

  resize(w: number, h: number) {
    if (w < 2 || h < 2) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.dirty = true;
  }

  private loop = () => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    if (this.controls.update()) this.dirty = true;
    if (!this.dirty) return;
    this.dirty = false;
    this.renderer.render(this.scene, this.camera);
  };

  /**
   * A still of a model for the filmstrip (rendered off screen at twice the size, scaled down for smooth edges).
   * The model is disposed afterwards. Returns a data: URL (WebP, PNG where WebP isn't available).
   */
  thumbnail(model: StageModel, w = 272, h = 204): string | null {
    const ss = 2, W = w * ss, H = h * ss;
    const scene = new THREE.Scene();
    const r = rig(model, this.shadowTex);
    scene.add(r.pivot);
    const cam = new THREE.PerspectiveCamera(FOV, w / h, 0.1, 10000);
    const target = new THREE.Vector3(0, r.height * 0.5, 0);
    // fit the bounding sphere into the narrower side
    const fit = Math.min(1, w / h);
    const dist = (r.radius / Math.sin(THREE.MathUtils.degToRad(FOV / 2))) / fit * 0.98;
    cam.position.copy(target).addScaledVector(viewDir(false), dist);
    cam.near = Math.max(0.05, dist / 500);
    cam.far = dist * 20;
    cam.lookAt(target);
    cam.updateProjectionMatrix();
    const rt = new THREE.WebGLRenderTarget(W, H);
    const px = new Uint8Array(W * H * 4);
    try {
      this.renderer.setRenderTarget(rt);
      this.renderer.clear();
      this.renderer.render(scene, cam);
      this.renderer.readRenderTargetPixels(rt, 0, 0, W, H, px);
    } finally {
      this.renderer.setRenderTarget(null);
      rt.dispose();
      r.dispose();
      model.dispose();
      this.dirty = true;
    }
    // rows come bottom-up: flip while copying into a canvas, then scale down
    const big = document.createElement("canvas");
    big.width = W; big.height = H;
    const img = new ImageData(W, H);
    for (let y = 0; y < H; y++) img.data.set(px.subarray((H - 1 - y) * W * 4, (H - y) * W * 4), y * W * 4);
    big.getContext("2d")!.putImageData(img, 0, 0);
    const out = document.createElement("canvas");
    out.width = w; out.height = h;
    const g = out.getContext("2d")!;
    g.imageSmoothingQuality = "high";
    g.drawImage(big, 0, 0, w, h);
    const webp = out.toDataURL("image/webp", 0.88);
    return webp.startsWith("data:image/webp") ? webp : out.toDataURL("image/png");
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.setModel(null);
    this.controls.dispose();
    this.mats.solid.dispose();
    this.mats.translucent.dispose();
    this.shared.uTex.value.dispose();
    this.shadowTex.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
